import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState, useContext } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { connectWallet, getContract } from "../../layouts/smart-contracts";
import { useOutletContext } from "react-router-dom";
import { AppContext } from "../../scripts/context/App";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const MODAL_STYLES = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 1200,
  minHeight: "80vh",
  bgcolor: "#fff",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};
const INPUTS_LIST: string[] = [
  "first_name",
  "last_name",
  "email",
  "mobile",
  "address.street",
  "address.city",
  "address.zip_code",
  "address.country",
];

interface IProps {
  data: any;
  handleClose: () => void;
  update: () => void;
}

const INITREJECTION_REASONS = {
  personal_information: {
    isOpen: false,
    reasons: "",
  },
  kyc: {
    isOpen: false,
    reasons: "",
  },
}

export default function PreviewUserModal({ data, update, handleClose: handleCloseProps }: IProps) {
  const { user, connectedAddress, setState } = useContext(AppContext);
  const {
    contracts: {
      claimer: { contract: claimerContract },
    },
  }: any = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [rejectionReasons, setRejectionReasons] = useState<any>(INITREJECTION_REASONS);

  const handleClose = () => {
    setRejectionReasons(INITREJECTION_REASONS);
    handleCloseProps();
  }

  const handleChange = (e: any) => {
    setRejectionReasons({
      ...rejectionReasons,
      [e.target.name]: {
        isOpen: true,
        reasons: e.target.value,
      },
    });
  };

  const submitModal = async () => {
    if (!user.permissions.includes("approve_reject_customer")) return toast.error("You don't have permission");

    try {
      if (
        connectedAddress.toLowerCase() !==
        process.env.REACT_APP_ADMIN_ADDRESS?.toLowerCase()
      )
        return toast.error(
          "You must connect with the admin address to use this action"
        );

      if (
        rejectionReasons.personal_information.reasons ||
        rejectionReasons.kyc.reasons
      ) {
        try {
          setLoading(true);
          const { data: responseData } = await axios.post("/customers/reject", {
            customer_id: data.id,
            KYC: rejectionReasons.kyc.reasons,
            personal_info: rejectionReasons.personal_information.reasons,
          });

          update();
        } catch (error: any) {
          toast.error(
            error.response.data.message ||
            "Something went wrong, please try again"
          );

          console.log({ error });
        }
        setLoading(false);
      } else {
        if (!data.wallet_address) return;
        const addClaimer = await claimerContract.addClaimer(
          [data.wallet_address],
          ["0x0000000000000000000000000000000000000000"]
        );

        toast.promise(
          addClaimer.wait().then(async ({ transactionHash: txID }: any) => {
            try {
              const requestResult = await axios.post("/customers/approve", {
                customer_id: data.id,
                txID,
              });

              update();
              toast.success(requestResult.data.message);
            } catch (error: any) {
              console.log(error, "error while trying to approve customer");

              toast.error(
                error.response.data.message ||
                "Something went wrong, please try again"
              );
            }
          }),
          {
            loading: "Adding...",
            success: "Added",
            error: "Something went wrong, please try again",
          }
        );
      }
    } catch (error) {
      console.log(error);
    }
    handleClose();
  };

  return (
    <Modal
      keepMounted
      open={data.status}
      onClose={handleClose}
      aria-labelledby="Preview-User-Modal"
    >
      <Box sx={MODAL_STYLES}>
        <Box sx={{ p: 3, borderBottom: "1px solid #d3d3d3" }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ wordBreak: "break-all" }}
          >
            Preview Customer Details
          </Typography>
        </Box>

        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            overflowY: "auto",
            maxHeight: "60vh",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box
              sx={{
                flex: "1 1 770px",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #d3d3d3",
                borderRadius: "2px",
                p: 3,
                gap: 2,
                height: "fit-content",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {INPUTS_LIST.map((key, index) => {
                  if (key.includes("address"))
                    return (
                      <TextField
                        sx={{ flex: "1 1 300px" }}
                        key={index}
                        id={`${key}-input`}
                        name={key.split(".")[1]}
                        value={data.address[key.split(".")[1]]}
                        label={(
                          key.split(".")[1].charAt(0).toUpperCase() +
                          key.split(".")[1].slice(1)
                        ).replace("_", " ")}
                        variant="standard"
                      />
                    );

                  return (
                    <TextField
                      sx={{ flex: "1 1 300px" }}
                      key={index}
                      id={`${key}-input`}
                      name={key}
                      value={data[key]}
                      label={key.replace("_", " ")}
                      variant="standard"
                    />
                  );
                })}
              </Box>
              <Button
                onClick={() => {
                  setRejectionReasons({
                    ...rejectionReasons,
                    personal_information: {
                      isOpen: !rejectionReasons.personal_information.isOpen,
                      reasons: "",
                    },
                  });
                }}
                variant={
                  rejectionReasons.personal_information.isOpen
                    ? "outlined"
                    : "contained"
                }
                color="error"
                size="small"
                sx={{ maxWidth: "100px" }}
              >
                {rejectionReasons.personal_information.isOpen
                  ? "Cancel"
                  : "Reject"}
              </Button>
              {rejectionReasons.personal_information.isOpen && (
                <TextField
                  id="outlined-multiline-static"
                  label="Reasons"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={rejectionReasons.personal_information.reasons}
                  onChange={handleChange}
                  name="personal_information"
                />
              )}
            </Box>
            <Box
              sx={{
                flex: "1 1 300px",
                border: "1px solid #d3d3d3",
                borderRadius: "2px",
                display: "flex",
                flexDirection: "column",
                p: 3,
                gap: 2,
                height: "fit-content",
              }}
            >
              {data.identity_type === "pdf" ? (
                <Box>
                  <Tooltip title="Open PDF File">
                    <IconButton aria-label="open pdf file" size="large" color="primary" onClick={async () => {
                      try {
                        const { data: response } = await axios.get(data.identity, { responseType: "blob" });

                        const fileURL = window.URL.createObjectURL(
                          new Blob([response], { type: "application/pdf" })
                        );
                        window.open(fileURL, "_blank");
                      } catch (error: any) {
                        console.log(error);

                        toast.error(
                          error.response.data.message || "Something went wrong, please try again"
                        )
                      }
                    }}>
                      <PictureAsPdfIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : <img
                src={data.identity}
                alt="identity"
                style={{ height: "200px", width: "200px", objectFit: "cover" }}
              />}
              <Button
                onClick={() => {
                  setRejectionReasons({
                    ...rejectionReasons,
                    kyc: {
                      isOpen: !rejectionReasons.kyc.isOpen,
                      reasons: "",
                    },
                  });
                }}
                variant={rejectionReasons.kyc.isOpen ? "outlined" : "contained"}
                color="error"
                size="small"
                sx={{ maxWidth: "100px" }}
              >
                {rejectionReasons.kyc.isOpen ? "Cancel" : "Reject"}
              </Button>
              {rejectionReasons.kyc.isOpen && (
                <TextField
                  id="outlined-multiline-static"
                  label="Reasons"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={rejectionReasons.personal_information.kyc}
                  onChange={handleChange}
                  name="kyc"
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #d3d3d3",
            p: 3,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={
              connectedAddress ? submitModal : () => connectWallet(setState)
            }
            disabled={loading}
            endIcon={loading && <CircularProgress size={20} />}
          >
            Confirm
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={loading}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
